"use client";

import { c as _c } from "react/compiler-runtime";
import { AuthContext } from "@/components/auth/Auth";
import { useCookieConsent } from "@/components/cookies/CookieConsentContext";
import useCapacitor from "@/hooks/useCapacitor";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { usePathname } from "next/navigation";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import UserPageTab from "./UserPageTab";
export enum UserPageTabType {
  BRAIN = "BRAIN",
  REP = "REP",
  IDENTITY = "IDENTITY",
  COLLECTED = "COLLECTED",
  STATS = "STATS",
  SUBSCRIPTIONS = "SUBSCRIPTIONS",
  PROXY = "PROXY",
  GROUPS = "GROUPS",
  WAVES = "WAVES",
  FOLLOWERS = "FOLLOWERS",
}
export const USER_PAGE_TAB_META: Record<UserPageTabType, {
  tab: UserPageTabType;
  title: string;
  route: string;
}> = {
  [UserPageTabType.BRAIN]: {
    tab: UserPageTabType.BRAIN,
    title: "Brain",
    route: ""
  },
  [UserPageTabType.REP]: {
    tab: UserPageTabType.REP,
    title: "Rep",
    route: "rep"
  },
  [UserPageTabType.IDENTITY]: {
    tab: UserPageTabType.IDENTITY,
    title: "Identity",
    route: "identity"
  },
  [UserPageTabType.COLLECTED]: {
    tab: UserPageTabType.COLLECTED,
    title: "Collected",
    route: "collected"
  },
  [UserPageTabType.STATS]: {
    tab: UserPageTabType.STATS,
    title: "Stats",
    route: "stats"
  },
  [UserPageTabType.SUBSCRIPTIONS]: {
    tab: UserPageTabType.SUBSCRIPTIONS,
    title: "Subscriptions",
    route: "subscriptions"
  },
  [UserPageTabType.PROXY]: {
    tab: UserPageTabType.PROXY,
    title: "Proxy",
    route: "proxy"
  },
  [UserPageTabType.GROUPS]: {
    tab: UserPageTabType.GROUPS,
    title: "Groups",
    route: "groups"
  },
  [UserPageTabType.WAVES]: {
    tab: UserPageTabType.WAVES,
    title: "Waves",
    route: "waves"
  },
  [UserPageTabType.FOLLOWERS]: {
    tab: UserPageTabType.FOLLOWERS,
    title: "Followers",
    route: "followers"
  }
};
export default function UserPageTabs() {
  const $ = _c(35);
  const pathname = usePathname() ?? "";
  const capacitor = useCapacitor();
  const {
    country
  } = useCookieConsent();
  const {
    showWaves
  } = useContext(AuthContext);
  const pathnameToTab = _temp;
  let t0;
  if ($[0] !== pathname) {
    t0 = pathnameToTab(pathname);
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [tab_1, setTab] = useState(t0);
  let t1;
  let t2;
  if ($[2] !== pathname) {
    t1 = () => {
      setTab(pathnameToTab(pathname));
    };
    t2 = [pathname];
    $[2] = pathname;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  const wrapperRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const contentContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  let t3;
  if ($[5] !== capacitor.isIos || $[6] !== country || $[7] !== showWaves) {
    t3 = () => {
      let allTabs = Object.values(UserPageTabType);
      if (capacitor.isIos && country !== "US") {
        allTabs = allTabs.filter(_temp2);
      }
      if (showWaves) {
        return allTabs;
      }
      return allTabs.filter(_temp3);
    };
    $[5] = capacitor.isIos;
    $[6] = country;
    $[7] = showWaves;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const getTabsToShow = t3;
  let t4;
  if ($[9] !== getTabsToShow) {
    t4 = getTabsToShow();
    $[9] = getTabsToShow;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const [tabsToShow, setTabsToShow] = useState(t4);
  let t5;
  let t6;
  if ($[11] !== getTabsToShow) {
    t5 = () => setTabsToShow(getTabsToShow());
    t6 = [getTabsToShow];
    $[11] = getTabsToShow;
    $[12] = t5;
    $[13] = t6;
  } else {
    t5 = $[12];
    t6 = $[13];
  }
  useEffect(t5, t6);
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      const container = scrollContainerRef.current;
      if (!container) {
        return;
      }
      const {
        scrollLeft,
        scrollWidth,
        clientWidth
      } = container;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    };
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const checkScroll = t7;
  let t8;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      const container_0 = scrollContainerRef.current;
      const contentContainer = contentContainerRef.current;
      if (!container_0) {
        return;
      }
      checkScroll();
      container_0.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScroll);
      const resizeObserver = new ResizeObserver(() => {
        checkScroll();
      });
      resizeObserver.observe(container_0);
      if (contentContainer) {
        resizeObserver.observe(contentContainer);
      }
      return () => {
        container_0.removeEventListener("scroll", checkScroll);
        window.removeEventListener("resize", checkScroll);
        resizeObserver.disconnect();
      };
    };
    t9 = [];
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  useEffect(t8, t9);
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      const container_1 = scrollContainerRef.current;
      if (!container_1) {
        return;
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          checkScroll();
        });
      });
    };
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== tabsToShow) {
    t11 = [tabsToShow];
    $[18] = tabsToShow;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  useEffect(t10, t11);
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      const container_2 = scrollContainerRef.current;
      if (!container_2) {
        return;
      }
      container_2.scrollBy({
        left: -150,
        behavior: "smooth"
      });
    };
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const scrollLeft_0 = t12;
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      const container_3 = scrollContainerRef.current;
      if (!container_3) {
        return;
      }
      container_3.scrollBy({
        left: 150,
        behavior: "smooth"
      });
    };
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const scrollRight = t13;
  let t14;
  if ($[22] !== tab_1 || $[23] !== tabsToShow) {
    t14 = tabsToShow.map(tabType => <UserPageTab key={tabType} tab={tabType} activeTab={tab_1} parentRef={wrapperRef} />);
    $[22] = tab_1;
    $[23] = tabsToShow;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t14) {
    t15 = <div ref={scrollContainerRef} className="tw-w-full tw-overflow-x-auto tw-overflow-y-hidden [touch-action:pan-x] [&::-webkit-scrollbar]:tw-hidden [-ms-overflow-style:none] [scrollbar-width:none]" aria-label="Tabs"><div ref={contentContainerRef} className="-tw-mb-px tw-flex tw-gap-x-3 lg:tw-gap-x-4 tw-min-w-max" aria-label="Tabs">{t14}</div></div>;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== canScrollLeft) {
    t16 = canScrollLeft && <><div className="tw-absolute tw-left-0 tw-top-0 tw-bottom-0 tw-w-24 tw-pointer-events-none tw-z-10 tw-bg-gradient-to-r tw-from-black tw-via-black/40 tw-to-black/0" /><button onClick={scrollLeft_0} aria-label="Scroll tabs left" className="tw-absolute tw-left-0 tw-top-1/2 tw--translate-y-1/2 tw-z-20 tw-inline-flex tw-items-center tw-justify-start tw-group tw-p-0 tw-h-10 tw-w-10 tw-bg-transparent tw-border-none tw-outline-none"><FontAwesomeIcon icon={faChevronLeft} className="tw-h-6 tw-w-6 tw-text-iron-200 group-hover:tw-text-iron-300 tw-transition tw-duration-300 tw-ease-out" /></button></>;
    $[27] = canScrollLeft;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== canScrollRight) {
    t17 = canScrollRight && <><div className="tw-absolute tw-right-0 tw-top-0 tw-bottom-0 tw-w-24 tw-pointer-events-none tw-z-10 tw-bg-gradient-to-l tw-from-black tw-via-black/40 tw-to-black/0" /><button onClick={scrollRight} aria-label="Scroll tabs right" className="tw-absolute tw-right-0 tw-top-1/2 tw--translate-y-1/2 tw-z-20 tw-inline-flex tw-items-center tw-justify-end tw-group tw-p-0 tw-h-10 tw-w-10 tw-bg-transparent tw-border-none tw-outline-none"><FontAwesomeIcon icon={faChevronRight} className="tw-h-6 tw-w-6 tw-text-iron-200 group-hover:tw-text-iron-300 tw-transition tw-duration-300 tw-ease-out" /></button></>;
    $[29] = canScrollRight;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t15 || $[32] !== t16 || $[33] !== t17) {
    t18 = <div className="tw-relative tw-overflow-hidden tw-border-b tw-border-iron-700 tw-border-solid tw-border-x-0 tw-border-t-0">{t15}{t16}{t17}</div>;
    $[31] = t15;
    $[32] = t16;
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  return t18;
}
function _temp3(tab_3) {
  return ![UserPageTabType.BRAIN, UserPageTabType.WAVES].includes(tab_3);
}
function _temp2(tab_2) {
  return tab_2 !== UserPageTabType.SUBSCRIPTIONS;
}
function _temp(pathname_0) {
  const segments = pathname_0.split("/").filter(Boolean);
  const name = segments[1] ?? "";
  const tab_0 = Object.values(UserPageTabType).find(tab => USER_PAGE_TAB_META[tab].route.toLowerCase() === name?.toLowerCase());
  return tab_0 ?? UserPageTabType.COLLECTED;
}